/**
 * experienceSkills.ts — the ONE skill taxonomy for the admin Experience tab's
 * by-skill sub-tabs (Onboarding / Ongoing / Celebrations / Requests).
 *
 * Base Ava (`platform`) + all 8 skills INCLUDING Story. Story is client-purchasable
 * via the BAS corporate site (fullStackMonthly = base + all 8); Ava merely never
 * *pitches* it (avaEverythingMonthly = 7, no Story). The Experience tab shows what
 * ANY client can experience, so Story is in. Locked John 2026-08-02 — see
 * strategy/experience-tab-arc.md § "Skill taxonomy".
 *
 * This is the single source so the three catalog sub-tabs can't drift into three
 * different skill lists (they used to each re-declare their own). Ids match the
 * celebration/canary `agent` field and the ongoing-cadence composer tags. Order +
 * labels are the ones Celebrations shipped, so adopting this changes nothing visible.
 */
export interface ExperienceSkill {
  /** Stable id — matches the `agent` field on celebrations / canary results / composer tags. */
  id: string
  /** Plain skill name, no icon. */
  label: string
  /** Leading emoji shown before the label. */
  icon: string
}

export const EXPERIENCE_SKILLS: ExperienceSkill[] = [
  { id: 'platform', label: 'Platform (cross-agent)', icon: '🏛️' }, // = Base Ava
  { id: 'google', label: 'Google Agent', icon: '🔍' },
  { id: 'phone', label: 'Phone Agent', icon: '📞' },
  { id: 'social', label: 'Social Agent', icon: '📣' },
  { id: 'web', label: 'Web Agent', icon: '🌐' },
  { id: 'ads', label: 'Ads Agent', icon: '📢' },
  { id: 'video', label: 'Video Agent', icon: '🎬' },
  { id: 'nurture', label: 'Nurture Agent', icon: '💬' },
  { id: 'story', label: 'Story Agent', icon: '🎙️' },
]

/** Skill ids in canonical display order. */
export const EXPERIENCE_SKILL_ORDER: string[] = EXPERIENCE_SKILLS.map((s) => s.id)

/** Section heading string (icon + label), e.g. "🏛️ Platform (cross-agent)". Falls back to the raw id. */
export function experienceSkillHeading(id: string): string {
  const s = EXPERIENCE_SKILLS.find((x) => x.id === id)
  return s ? `${s.icon} ${s.label}` : id
}

/**
 * Normalize a raw `agent` value from the test corpus (or an agent log) to one of the
 * canonical EXPERIENCE_SKILLS ids. The corpus `agent` field is messy — it mixes clean
 * ids ('ads', 'phone') with display labels ('Ava (Base)', 'Email Triage', 'Phone
 * Agent', 'researchReport'). This maps them to the by-skill grouping the Experience
 * tab uses. Anything Ava-general / cross-cutting / base falls to 'platform' (Base Ava),
 * which is the correct bucket for it. Heuristic by design — keep the mapping HERE so
 * the sub-tabs never each reinvent it.
 */
export function normalizeToExperienceSkill(agent: string): string {
  const a = (agent || '').trim().toLowerCase()
  if (EXPERIENCE_SKILL_ORDER.includes(a)) return a // already a clean skill id
  if (a.includes('phone') || a.includes('inbound voice') || a.includes('receptionist')) return 'phone'
  if (a.includes('google')) return 'google'
  if (a.includes('social')) return 'social'
  if (a.includes('nurture')) return 'nurture'
  if (a.includes('video')) return 'video'
  if (a.includes('story')) return 'story'
  if (a === 'ads' || a.includes('ad ') || a.includes('ads')) return 'ads'
  if (a.includes('web')) return 'web'
  // ava / base / email-triage / research / workforce / anything else → Base Ava
  return 'platform'
}
